import { get, merge, set } from 'lodash';
import moment from 'moment';

// local dependencies
import { HttpService } from 'core/modules/network-service';
import { SERVICE_RESPONSE_STATUS } from 'constants/common';
import { getStore } from 'store';
import { REDUCER, MEMBER_SEARCH_MODULE } from 'constants/entity-names';

// get store
const store = getStore();

/*************************************************************/

/**
 * @type {class}
 * @desc MemberSearchWidget defines an HTTP service interface to send HTTP network requets.
 * @example
 * import { MemberSearchWidgetService } from 'services/memberSearchWidget';
 *
 * // create service instance and send/cancel request
 * const memberSearchWidgetService = new MemberSearchWidgetService();
 * const result = await memberSearchWidgetService.fetchData( '<user-id>' ); // fetch data
 * memberSearchWidgetService.cancel(); // cancel an active request
 */
export class MemberSearchWidgetService extends HttpService {

    // The server verifies the member and resolves the patient before navigation.
    async selectLiveMember( { url, data } ) {
        try {
            const response = await this.post( { host: CONFIG.host, path: url, data } );
            return {
                status: get( response, 'body.status', SERVICE_RESPONSE_STATUS.FAILURE ),
                data: get( response, 'body.component.data', null ),
            };
        } catch( error ) {
            return { status: SERVICE_RESPONSE_STATUS.FAILURE, data: null };
        }
    }

    // Collision recovery is independently verified by the server.
    async selectIndexedCollision( { url, data } ) {
        return this.selectLiveMember( { url, data } );
    }

    // make request and fetch component data
    async fetchComponentData( { url = {}, data = {} } = {} ) {

        // get ongoing call info from the redux store
        const callId = get( store.getState()[ REDUCER.CALL_STATE ], 'callId', null );
        const callType = get( store.getState()[ REDUCER.CALL_STATE ], 'callType', null );
        
        try {
            const response = await this.post( {
                host: CONFIG.host,
                path: url,
                data: merge( data, {
                    context: {
                        callId,
                        callType,
                    },
                } ),
            } );
            return {
                status: get( response, 'body.status', SERVICE_RESPONSE_STATUS.FAILURE ),
                data: get( response, 'body.component', {} ),
                isRoleAvailable:  get( response, 'body.component.data.isRoleAvailable', false ),
            };
        } catch( error ) {
            return {
                status: SERVICE_RESPONSE_STATUS.FAILURE,
                data: null,
            };
        }
    }

    // initiate an outbound call (for member)
    async initiateOutboundCall( { url = {}, data = {} } = {} ) {

        try {
            const response = await this.post( {
                host: CONFIG.host,
                path: url,
                data: data
            } );

            return {
                status: get( response, 'body.status', SERVICE_RESPONSE_STATUS.FAILURE ),
                data: {
                    ...get( response, 'body.component', {} ),
                    ...get( response, 'body.component.structure', {} )
                }
            };
        } catch( error ) {
            return {
                status: SERVICE_RESPONSE_STATUS.FAILURE,
                data: null,
            };
        }
    }

    // start an outbound call (for member)
    async startOutboundCall( { url = {}, data = {} } = {} ) {

        // get ongoing call info from the redux store
        const callId = get( store.getState()[ REDUCER.CALL_STATE ], 'callId', null );
        const callType = get( store.getState()[ REDUCER.CALL_STATE ], 'callType', null );
        const module = MEMBER_SEARCH_MODULE.DASHBOARD;
        
        // const callerType= getCallerTypeFromName( 'member' );

        try {
            const response = await this.post( {
                host: CONFIG.host,
                path: url,
                data: merge( data, {
                    context: {
                        callId,
                        callType,
                        module,

                        // callerType
                    },
                } ),
            } );

            // set the callStartTime to local UTC time
            set( response, 'body.component.data.state.callStartTime', moment.utc().format() );
    
            return {
                status: get( response, 'body.status', SERVICE_RESPONSE_STATUS.FAILURE ),
                data: {
                    callData: get( response, 'body.component.data.state', {} )
                },
            };
        } catch( error ) {
            return {
                status: SERVICE_RESPONSE_STATUS.FAILURE,
                data: null,
            };
        }
    }

}
